import type { SelectHTMLAttributes } from 'react'
import { getTimezoneOffsetLabel, listTimezones, timezoneLabel } from './timezone'

/** Default styling — replaced entirely (not merged) when `className` is provided. */
const DEFAULT_SELECT_CLASSES =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50'

/** Props for {@link TimezoneSelect}. */
export interface TimezoneSelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'value' | 'onChange'
> {
  /** Currently selected IANA timezone (e.g. `"Asia/Tokyo"`). */
  value: string
  /** Called with the newly selected IANA timezone. */
  onChange: (timezone: string) => void
}

type TimezoneOption = { zone: string; label: string }

/**
 * Module-level cache of labelled timezone options.
 *
 * Building ~450 options calls `Intl.DateTimeFormat` once per zone. Doing that on
 * every React render (e.g. each keystroke in a parent form) is expensive enough
 * to blow Vitest's default 5s timeout under coverage. The zone list and current
 * UTC-offset labels are stable for the lifetime of the page, so compute once.
 */
let cachedOptions: TimezoneOption[] | null = null

/** City + offset label for a stored zone that is not in the shortened picker list. */
function orphanTimezoneLabel(zone: string): string {
  const city = zone.slice(zone.lastIndexOf('/') + 1).replace(/_/g, ' ')
  const offset = getTimezoneOffsetLabel(zone)
  return offset ? `${city} (${offset})` : city
}

function getTimezoneOptions(): TimezoneOption[] {
  if (!cachedOptions) {
    cachedOptions = listTimezones().map((zone) => ({
      zone,
      label: timezoneLabel(zone),
    }))
  }
  return cachedOptions
}

/**
 * Shared IANA timezone picker. Lists one representative timezone per current UTC
 * offset (e.g. a single `UTC+02:00` city), each labelled with its fixed abbreviation and
 * that offset (e.g. `"CEST (UTC+02:00)"`, `"JST (UTC+09:00)"`) so the menu stays short
 * and scannable. These abbreviations are intentionally the same string in every UI
 * language — by product decision, they're written/recognized identically everywhere
 * (nobody translates "CEST" into Japanese) — see {@link getTimezoneAbbreviation}.
 *
 * The current `value` is always shown even when another zone already covers the
 * same offset (so an existing event keeps its stored IANA id).
 *
 * Use `getDeviceTimezone()` to default `value` to the timezone detected from the device.
 */
export function TimezoneSelect({
  value,
  onChange,
  className = DEFAULT_SELECT_CLASSES,
  ...rest
}: TimezoneSelectProps) {
  const zones = getTimezoneOptions()
  // Always render the current value even if it fell outside the supported list.
  const options = zones.some((o) => o.zone === value)
    ? zones
    : [
        {
          zone: value,
          label: orphanTimezoneLabel(value),
        },
        ...zones,
      ]

  return (
    <select
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={className}
    >
      {options.map(({ zone, label }) => (
        <option key={zone} value={zone}>
          {label}
        </option>
      ))}
    </select>
  )
}
